import React, { useState, useRef } from 'react';
import { Button, Spinner, OverlayTrigger, Tooltip, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faCheck, faExclamationTriangle, faImage } from '@fortawesome/free-solid-svg-icons';
import { fileStorage, db } from 'config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { toast } from 'react-toastify';

/**
 * ManualImageUpload - Inline upload button for products that need manual images.
 *
 * Features:
 * - Drag and drop or click to select image
 * - Uploads to Firebase Storage
 * - Updates product document with sourced image data
 * - Shows progress spinner during upload
 * - Success state with checkmark after upload
 *
 * @param {string} sku - Product SKU
 * @param {string} productName - Product name for file naming
 * @param {function} onUploadComplete - Callback when upload succeeds
 * @param {boolean} compact - Show compact version
 */
const ManualImageUpload = ({ sku, productName, onUploadComplete, compact = false }) => {
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  /**
   * Handles file selection and upload.
   */
  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file', { theme: 'colored' });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be smaller than 10MB', { theme: 'colored' });
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Create unique filename
      const timestamp = Date.now();
      const safeSku = sku.replace(/[^a-zA-Z0-9]/g, '_');
      const extension = file.name.split('.').pop() || 'jpg';
      const filename = `${safeSku}_manual_${timestamp}.${extension}`;
      const storagePath = `productCatalog/topicz/products/${safeSku}/images/${filename}`;

      // Upload to Firebase Storage
      const storageReference = ref(fileStorage, storagePath);
      const snapshot = await uploadBytes(storageReference, file);
      const downloadUrl = await getDownloadURL(snapshot.ref);

      // Update product document with sourced image
      const productRef = doc(db, 'productCatalog/topicz/products', safeSku);
      await updateDoc(productRef, {
        sourcedImages: arrayUnion({
          url: downloadUrl,
          source: 'manual',
          addedAt: new Date().toISOString(),
          filename: filename,
          dimensions: null, // Will be populated by backend validation
          validated: false
        })
      });

      setUploaded(true);
      toast.success(`Image uploaded for ${sku}`, { theme: 'colored' });

      if (onUploadComplete) {
        onUploadComplete({
          sku,
          url: downloadUrl,
          source: 'manual'
        });
      }

      // Reset after 3 seconds
      setTimeout(() => {
        setUploaded(false);
      }, 3000);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message);
      toast.error(`Upload failed: ${err.message}`, { theme: 'colored' });
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  /**
   * Trigger file input click.
   */
  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Determine button state and appearance
  const getButtonState = () => {
    if (uploading) {
      return {
        variant: 'outline-primary',
        icon: <Spinner animation="border" size="sm" />,
        text: compact ? '' : 'Uploading...',
        disabled: true
      };
    }
    if (uploaded) {
      return {
        variant: 'success',
        icon: <FontAwesomeIcon icon={faCheck} />,
        text: compact ? '' : 'Uploaded',
        disabled: true
      };
    }
    if (error) {
      return {
        variant: 'outline-danger',
        icon: <FontAwesomeIcon icon={faExclamationTriangle} />,
        text: compact ? '' : 'Retry',
        disabled: false
      };
    }
    return {
      variant: 'outline-secondary',
      icon: <FontAwesomeIcon icon={faUpload} />,
      text: compact ? '' : 'Upload Image',
      disabled: false
    };
  };

  const state = getButtonState();

  const button = (
    <Button
      variant={state.variant}
      size="sm"
      onClick={handleClick}
      disabled={state.disabled}
      className={compact ? 'p-1' : ''}
      style={compact ? { minWidth: '32px' } : {}}
    >
      <span className={state.text ? 'me-1' : ''}>{state.icon}</span>
      {state.text}
    </Button>
  );

  return (
    <>
      <Form.Control
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: 'none' }}
      />
      {compact ? (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`upload-tooltip-${sku}`}>
              {uploading ? 'Uploading...' : uploaded ? 'Uploaded!' : error ? 'Click to retry' : 'Upload image manually'}
            </Tooltip>
          }
        >
          {button}
        </OverlayTrigger>
      ) : (
        button
      )}
    </>
  );
};

/**
 * ManualImageUploadRow - Compact row component for use in tables/lists.
 *
 * @param {object} product - Product object with sku, name
 * @param {function} onUploadComplete - Callback when upload succeeds
 */
const ManualImageUploadRow = ({ product, onUploadComplete }) => {
  return (
    <div className="d-flex align-items-center justify-content-between py-2 px-3 border-bottom">
      <div className="d-flex align-items-center">
        {product.primaryImage ? (
          <img
            src={product.primaryImage}
            alt={product.name}
            style={{ width: 40, height: 40, objectFit: 'cover' }}
            className="rounded me-2"
          />
        ) : (
          <div
            className="bg-light rounded me-2 d-flex align-items-center justify-content-center"
            style={{ width: 40, height: 40 }}
          >
            <FontAwesomeIcon icon={faImage} className="text-muted" />
          </div>
        )}
        <div>
          <div className="fw-medium small">{product.name || product.sku}</div>
          <code className="text-muted" style={{ fontSize: '0.75rem' }}>{product.sku}</code>
        </div>
      </div>
      <ManualImageUpload
        sku={product.sku}
        productName={product.name}
        onUploadComplete={onUploadComplete}
        compact
      />
    </div>
  );
};

export { ManualImageUpload, ManualImageUploadRow };
export default ManualImageUpload;
